import {useCallback, useMemo, useState} from 'react';
import {AdminService} from '@/modules/admin/services/adminService';
import {getBaseDomain} from '@/shared/utils/domain';
import {decodeJwtToken} from '@/shared/utils/tokenUtils';
import {isOrganizationRole, isSuperAdministratorRole} from '@mld/shared/constants/userRoles';
import {useAuth} from '@/modules/auth/hooks/useAuth';

export interface ProviderConsoleSwitchOptions {
    orgId: number | null;
    subdomain?: string | null;
    returnState?: Record<string, unknown>;
}

export function useProviderConsoleSwitch() {
    const {user} = useAuth();
    const [switchingProviderId, setSwitchingProviderId] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);

    const baseDomain = useMemo(() => {
        if (typeof window === 'undefined') return '';
        return getBaseDomain(window.location.hostname);
    }, []);

    const canSwitch = useMemo(() => {
        if (!user?.token) return false;
        const payload = decodeJwtToken(user.token);
        return isOrganizationRole(payload?.role_id) || isSuperAdministratorRole(payload?.role_id);
    }, [user?.token]);

    const switchToProvider = useCallback(
        async (providerId: number, options: ProviderConsoleSwitchOptions) => {
            if (!canSwitch) return;
            setError(null);
            setSwitchingProviderId(providerId);
            try {
                const response = await AdminService.createProviderSwitchToken(providerId, options.orgId);

                // Store return token and state in base-domain cookies so the
                // provider console can read them on any subdomain.
                const cookieDomain = baseDomain && baseDomain !== 'localhost' ? `; domain=.${baseDomain}` : '';
                document.cookie = `mld_provider_return=${encodeURIComponent(response.return_token)}; path=/; max-age=120; SameSite=Lax${cookieDomain}`;
                if (options.returnState) {
                    document.cookie = `mld_provider_state=${encodeURIComponent(JSON.stringify(options.returnState))}; path=/; max-age=120; SameSite=Lax${cookieDomain}`;
                }

                // Exchange sets the httpOnly refresh cookie scoped to the base
                // domain.  The target page bootstraps auth from this cookie on
                // load, so no login() call is needed.
                await AdminService.exchangeProviderSwitchToken(response.token);

                const portSuffix = window.location.port ? `:${window.location.port}` : '';
                const targetSubdomain = options.subdomain ?? undefined;
                const targetHost = targetSubdomain && baseDomain
                    ? `${targetSubdomain}.${baseDomain}${portSuffix}`
                    : window.location.host;

                window.location.assign(`${window.location.protocol}//${targetHost}/provider`);
            } catch (err: any) {
                const message = err?.response?.data?.error || 'Failed to open provider console.';
                setError(message);
                setSwitchingProviderId(null);
            }
        },
        [baseDomain, canSwitch]
    );

    return {switchToProvider, switchingProviderId, canSwitch, error, clearError: () => setError(null)};
}
